import type { FormSchemaV2 } from './agentShared/formSchema';
import { getAuthToken } from './apiClient';

// The agent plane is a separate service (extractable microservice) — not the
// CRUD backend. Point VITE_AGENT_URL at it; defaults to local dev.
export const AGENT_BASE_URL: string =
  (import.meta.env.VITE_AGENT_URL as string | undefined) ?? 'http://localhost:4100';

const authHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * A failed agent-plane request, shaped like an AxiosError on purpose (story
 * 4-2e).
 *
 * G-27 makes the SERVER's sentence the user-facing one and puts the single
 * unwrap point in `lib/errorMessage.ts`. That helper reads `err.response
 * .data.message`; the agent plane is reached by hand-rolled `fetch` (it is a
 * separate service and deliberately not in the generated client) and answers
 * `{error: '…'}`, so before this class its refusals arrived as a bare `new
 * Error(body.error)` — which `errorMessage()` cannot see into, so every agent
 * failure rendered as the generic fallback and the real reason ("that PDF has
 * no text layer") was thrown away. Mirroring axios's envelope means ONE
 * unwrap point still serves both planes.
 */
export class AgentRequestError extends Error {
  readonly response: { status: number; data: { message: string } };

  constructor(status: number, serverMessage?: string) {
    const message = serverMessage && serverMessage.length > 0 ? serverMessage : `Request failed (${status})`;
    super(message);
    this.name = 'AgentRequestError';
    this.response = { status, data: { message } };
  }
}

// Batched plans (story 1D-2) always resolve as 'approved' even when a step
// failed partway — result.ok tells the caller whether every step actually
// landed (story 2-26: the panel needs this to render an honest partial-
// failure line instead of a bare "approved" that hides the stoppage).
export interface ApprovalStepOutcome {
  step: number;
  tool: string;
  status: 'ok' | 'failed';
  error?: string;
}
export interface ApprovalOutcome {
  status: string;
  reason?: string;
  result?: {
    ok?: boolean;
    stoppedAtStep?: number;
    steps?: ApprovalStepOutcome[];
  };
}

export async function resolveApproval(token: string, approved: boolean): Promise<ApprovalOutcome> {
  const res = await fetch(`${AGENT_BASE_URL}/v1/agents/approvals/${token}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ approved }),
  });
  return res.json() as Promise<ApprovalOutcome>;
}

export async function fetchDraft(draftId: string): Promise<{ draft_id: string; schema: FormSchemaV2 } | null> {
  const res = await fetch(`${AGENT_BASE_URL}/v1/agents/drafts/${draftId}`, { headers: authHeaders() });
  if (!res.ok) return null;
  return res.json() as Promise<{ draft_id: string; schema: FormSchemaV2 }>;
}

export interface AgentInfo {
  name: string;
  displayName: string;
  internal?: boolean;
}

// User-facing agents only — internal (dev-facing) agents like the design
// system assistant stay reachable by name but never appear in switchers.
export async function fetchAgents(): Promise<AgentInfo[]> {
  const res = await fetch(`${AGENT_BASE_URL}/v1/agents`);
  if (!res.ok) return [];
  const body = (await res.json()) as { agents?: AgentInfo[] };
  return (body.agents ?? []).filter((a) => !a.internal);
}

export interface SavedStyle {
  name: string;
  updatedAt?: string;
  pageSize?: string;
  thumbnailSize?: string;
  layout?: unknown;
}

export async function listReportStyles(): Promise<SavedStyle[]> {
  const res = await fetch(`${AGENT_BASE_URL}/v1/agents/report-styles`, { headers: authHeaders() });
  if (!res.ok) return [];
  const body = (await res.json()) as { styles?: SavedStyle[] };
  return body.styles ?? [];
}

export async function saveReportStyle(name: string, layout: unknown): Promise<void> {
  await fetch(`${AGENT_BASE_URL}/v1/agents/report-styles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ name, layout }),
  });
}

export interface ImportResult {
  import_id: string;
  filename: string;
  pages: number;
  chars: number;
  preview: string;
}

// Upload a sample PDF to the agent service; the agent reads the extracted
// text via import_get_text and rebuilds it as a template with full precision.
export async function uploadImport(file: File): Promise<ImportResult> {
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
  const res = await fetch(`${AGENT_BASE_URL}/v1/agents/imports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ filename: file.name, data }),
  });
  const body = (await res.json()) as ImportResult & { error?: string };
  if (!res.ok) {
    throw new AgentRequestError(res.status, body?.error);
  }
  return body;
}
